import React from 'react';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, Typography, useMediaQuery } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { FieldError } from '@spike/model';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import { MarketplaceTax } from '@spike/marketplace-model';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { useSingleSaleTableItemStyles } from 'components/Payments/SingleSale/Table/Item/SingleSaleTableItem.styles';
import {
    GenericOrderItem,
    getOrderItemAmount,
    getOrderItemSubtotal,
    isAddOnServiceOrderItem as isAddOnServiceOrderItemType,
    isProductOrderItem as isProductOrderItemType,
    updateOrderItemDiscount,
    updateOrderItemQuantity
} from 'components/Payments/SingleSale/SingleSale';
import { SingleSaleTableItemSelector } from 'components/Payments/SingleSale/Table/Item/Selector/SingleSaleTableItemSelector';
import { SingleSaleTableItemType } from 'components/Payments/SingleSale/Table/Item/Type/SingleSaleTableItemType';
import { SingleSaleTableItemQuantity } from 'components/Payments/SingleSale/Table/Item/Quantity/SingleSaleTableItemQuantity';
import { SingleSaleTableItemDiscount } from 'components/Payments/SingleSale/Table/Item/Discount/SingleSaleTableItemDiscount';
import { SingleSaleTableItemTaxes } from 'components/Payments/SingleSale/Table/Item/Taxes/SingleSaleTableItemTaxes';
import { SingleSaleTableItemSubtotal } from 'components/Payments/SingleSale/Table/Item/Subtotal/SingleSaleTableItemSubtotal';
import { Discount, OrderizableTypes } from '@spike/order-model';
import { SingleSaleTableItemPrice } from 'components/Payments/SingleSale/Table/Item/Price/SingleSaleTableItemPrice';
import SelectStaff from 'components/CreateBooking/StaffSelector/SelectStaff';
import Staff from 'model/Staff';
import { useDispatch, useSelector } from 'react-redux';
import { showError } from '@spike/notifications-action';
import { RootState } from 'store';
import { Spinner } from 'components/UI';
import { getTrackedOnHand, insufficientStockMessage } from 'utils/productOnHand';

interface SingleSaleTableItemProps {
    item: GenericOrderItem;
    errors?: Array<FieldError>;
    selectedProductIds: Array<number>;
    selectedAddOnServiceIds: Array<number>;
    deleting?: boolean;
    onClick?: () => void;
    onChange: (orderItem: GenericOrderItem) => void;
    onRemove: (uuid: string) => void;
}

export const SingleSaleTableItem: React.FC<SingleSaleTableItemProps> = props => {
    const classes = useSingleSaleTableItemStyles();
    const dispatch = useDispatch();

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    const orderItemProduct = React.useMemo(
        () => (isProductOrderItemType(props.item) ? props.item.product : null),
        [props.item]
    );

    const orderItemAddOnService = React.useMemo(
        () => (isAddOnServiceOrderItemType(props.item) ? props.item.addOnService : null),
        [props.item]
    );

    const hasError =
        props.errors && props.errors.some(error => error.fieldName === `${props.item.orderItem.uuid}_duplicated`);

    const onChangeQuantityHandler = (quantity: number) => {
        const isIncreasing = quantity > props.item.orderItem.quantity;

        if (isIncreasing && isProductOrderItemType(props.item) && props.item.product) {
            const onHand = getTrackedOnHand(props.item.product);

            if (onHand !== undefined && quantity > onHand) {
                dispatch(showError(insufficientStockMessage(onHand)));
                return;
            }
        }

        props.onChange(updateOrderItemQuantity(props.item, quantity));
    };

    const changeSelectorHandler = (
        type: OrderizableTypes,
        product?: Product,
        addOnService?: AddOnService,
        description?: string
    ) => {
        if (type === OrderizableTypes.PRODUCT) {
            const onHand = getTrackedOnHand(product);
            const exceedsOnHand = onHand !== undefined && props.item.orderItem.quantity > onHand;
            const quantity = exceedsOnHand ? onHand : props.item.orderItem.quantity;

            if (exceedsOnHand) {
                dispatch(showError(insufficientStockMessage(onHand)));
            }

            const newOrderItem = {
                ...props.item,
                product,
                orderItem: {
                    ...props.item.orderItem,
                    quantity
                }
            };

            props.onChange({
                ...newOrderItem,
                orderItem: {
                    ...newOrderItem.orderItem,
                    amount: getOrderItemAmount(newOrderItem, true),
                    total: getOrderItemSubtotal(newOrderItem, true),
                    description:
                        (isEmpty(product?.businessProduct?.name) ? product?.name : product?.businessProduct?.name) || ''
                }
            });
        }

        if (type === OrderizableTypes.ADDON_SERVICE && addOnService) {
            const newOrderItem = { ...props.item, addOnService };

            props.onChange({
                ...props.item,
                addOnService,
                orderItem: {
                    ...props.item.orderItem,
                    description: addOnService.name || '',
                    amount: getOrderItemAmount(newOrderItem, true),
                    total: getOrderItemSubtotal(newOrderItem, true)
                }
            });
        }

        if (type === OrderizableTypes.CUSTOM) {
            props.onChange({
                ...props.item,
                orderItem: {
                    ...props.item.orderItem,
                    description: description || ''
                }
            });
        }
    };

    const changeSubtotalHandler = (subtotal: number | undefined) => {
        const newOrderItem = {
            ...props.item,
            orderItem: { ...props.item.orderItem, amount: subtotal ? subtotal.toString() : '0.00' }
        };

        props.onChange({
            ...newOrderItem,
            orderItem: {
                ...newOrderItem.orderItem,
                total: getOrderItemSubtotal(newOrderItem, true)
            }
        });
    };

    const changeDiscountHandler = (discount: Discount) => {
        props.onChange(updateOrderItemDiscount(props.item, discount));
    };

    const changeTaxHandler = (tax: MarketplaceTax | undefined) => {
        props.onChange({
            ...props.item,
            orderItem: {
                ...props.item.orderItem,
                taxId: tax?.id
            }
        });
    };

    const staffOptions = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const activeStaff = React.useMemo<Array<Staff>>(() => {
        return staffOptions.filter(member => member.active && !member.deleted);
    }, [staffOptions]);
    const selectedStaff = React.useMemo(
        () => activeStaff.find(s => s.id === props.item.orderItem.staffId) ?? undefined,
        [activeStaff, props.item.orderItem.staffId]
    );

    const changeStaffHandler = (staff: Staff) => {
        props.onChange({
            ...props.item,
            orderItem: {
                ...props.item.orderItem,
                staffId: staff.id
            }
        });
    };

    return (
        <>
            {isMobile && (
                <>
                    <div className={classes.gridContainer}>
                        <div className={classes.mobileGridHeader}>
                            <div className={classes.mobileGridTwoColumns}>
                                {(orderItemProduct?.id || orderItemAddOnService?.id) && (
                                    <Typography className={classes.mobileDescription}>
                                        {props.item.orderItem.description}
                                    </Typography>
                                )}

                                <SingleSaleTableItemSelector
                                    item={props.item}
                                    selectedProductIds={props.selectedProductIds}
                                    selectedAddOnServiceIds={props.selectedAddOnServiceIds}
                                    onChange={changeSelectorHandler}
                                />

                                <SingleSaleTableItemPrice
                                    item={props.item}
                                    errors={props.errors}
                                    className={classes.mobilePrice}
                                    inputClassName={clsx(classes.field, {
                                        [classes.individualFieldPrice]:
                                            props.item.orderItem.orderizable.type === OrderizableTypes.CUSTOM
                                    })}
                                    onChange={changeSubtotalHandler}
                                />
                            </div>

                            <div className={classes.mobileGridTwoColumns}>
                                <SingleSaleTableItemType type={props.item.orderItem.orderizable.type} />

                                <SingleSaleTableItemQuantity
                                    value={props.item.orderItem.quantity}
                                    onChange={onChangeQuantityHandler}
                                />
                            </div>
                            <div className={clsx(classes.mobileGridTwoColumns, classes.mobileGridRow)}>
                                <Typography className={classes.mobileGridTableLabel}>Staff</Typography>
                                <SelectStaff
                                    options={activeStaff}
                                    selectedOption={selectedStaff}
                                    onSelect={changeStaffHandler}
                                    placeholder="Select Staff"
                                    className={classes.boxCell}
                                />
                            </div>
                        </div>

                        <div className={classes.mobileGridTable}>
                            <div className={clsx(classes.mobileGridTwoColumns, classes.mobileGridRow)}>
                                <Typography className={classes.mobileGridTableLabel}>Discount</Typography>

                                <SingleSaleTableItemDiscount
                                    errors={props.errors}
                                    discount={props.item.orderItem.discount}
                                    name={`${props.item.orderItem.uuid}_discount`}
                                    onChange={changeDiscountHandler}
                                />
                            </div>

                            <div className={clsx(classes.mobileGridTwoColumns, classes.mobileGridRow)}>
                                <Typography className={classes.mobileGridTableLabel}>Tax</Typography>

                                <SingleSaleTableItemTaxes
                                    item={props.item}
                                    onChange={changeTaxHandler}
                                />
                            </div>
                            <div className={clsx(classes.mobileGridTwoColumns, classes.mobileGridRow)}>
                                <Typography
                                    className={classes.mobileGridTableLabel}
                                    style={{ fontWeight: '600' }}
                                >
                                    Total
                                </Typography>

                                <span style={{ fontWeight: '600' }}>
                                    <SingleSaleTableItemSubtotal
                                        item={props.item}
                                        errors={props.errors}
                                        onChange={changeSubtotalHandler}
                                    />
                                </span>
                            </div>
                        </div>

                        <div className={classes.mobileGridFooter}>
                            <button
                                type="button"
                                aria-label="Remove item"
                                className={classes.mobileDeleteButton}
                                disabled={props.deleting}
                                onClick={() => props.onRemove(props.item.orderItem.uuid)}
                            >
                                {props.deleting ? (
                                    <Spinner
                                        className={classes.deleteSpinner}
                                        classNameCircularProgress={classes.deleteSpinnerCircular}
                                    />
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {!isMobile && (
                <Grid
                    container
                    key={props.item.orderItem.uuid}
                    className={clsx(classes.gridContainer, { [classes.errorBackground]: hasError })}
                >
                    <Grid
                        item
                        md={2}
                        className={classes.gridCellName}
                    >
                        {(orderItemProduct || orderItemAddOnService) && (
                            <Typography>{props.item.orderItem.description}</Typography>
                        )}

                        <SingleSaleTableItemSelector
                            item={props.item}
                            errors={props.errors}
                            selectedProductIds={props.selectedProductIds}
                            selectedAddOnServiceIds={props.selectedAddOnServiceIds}
                            onChange={changeSelectorHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemType type={props.item.orderItem.orderizable.type} />
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={classes.gridCenter}
                    >
                        <SelectStaff
                            options={activeStaff}
                            selectedOption={selectedStaff}
                            onSelect={changeStaffHandler}
                            placeholder="Staff"
                            className={classes.boxCell}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemQuantity
                            value={props.item.orderItem.quantity}
                            onChange={onChangeQuantityHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemDiscount
                            discount={props.item.orderItem.discount}
                            name={`${props.item.orderItem.uuid}_discount`}
                            errors={props.errors}
                            onChange={changeDiscountHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemTaxes
                            item={props.item}
                            onChange={changeTaxHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemSubtotal
                            item={props.item}
                            errors={props.errors}
                            onChange={changeSubtotalHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={classes.gridCenter}
                    >
                        <div>
                            {props.deleting ? (
                                <Spinner
                                    className={classes.deleteSpinner}
                                    classNameCircularProgress={classes.deleteSpinnerCircular}
                                />
                            ) : (
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className={classes.closeIcon}
                                    onClick={() => props.onRemove(props.item.orderItem.uuid)}
                                />
                            )}
                        </div>
                    </Grid>
                </Grid>
            )}
        </>
    );
};
